import { parseValidationRows } from '@/features/projects/components/csvValidationFileParser';
import {
  buildHeaderMapping,
  buildStatusMapping,
  fieldOptions,
} from '@/features/projects/components/projectCsvMappingSectionShared';
import { buildValidationPreviewData } from '@/features/projects/components/validationDataChecks';
import { getErrorCopy } from '@/shared/copy/errors';
import {
  defaultCsvHeaderAliases,
  defaultCsvStatusAliases,
  type CsvHeaderAliases,
  type CsvStatusAliases,
} from '@flowqa/shared';

type ValidationStatus = 'unknown' | 'valid' | 'invalid';

type ValidateResultsSpreadsheetFileOptions = {
  headerAliases?: CsvHeaderAliases;
  statusAliases?: CsvStatusAliases;
};

type ResultsSpreadsheetValidationResult = {
  status: 'valid' | 'invalid';
  reason: string | null;
};

export type ResultsSpreadsheetRunEstimate = {
  suiteRuns: number;
  testRuns: number;
};

const statusFieldDefinitions = [
  { key: 'passed', label: 'Passed' },
  { key: 'failed', label: 'Failed' },
  { key: 'errored', label: 'Errored' },
  { key: 'skipped', label: 'Skipped' },
  { key: 'untested', label: 'Untested' },
] as const;

const requiredHeaderLabels = new Set(['Test', 'Status']);

const normalizeHeader = (value: string): string => value.trim().toLowerCase();

const normalizeStatus = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const parseAliasList = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

const resolveHeaderValidation = (
  headers: string[],
  draftMapping: Record<string, string>,
): Record<string, ValidationStatus> => {
  const headerSet = new Set(headers.map(normalizeHeader).filter((header) => header.length > 0));

  return fieldOptions.reduce<Record<string, ValidationStatus>>((acc, field) => {
    const aliases = parseAliasList(draftMapping[field.key]).map(normalizeHeader);
    if (aliases.length === 0) {
      acc[field.key] = 'unknown';
      return acc;
    }
    acc[field.key] = aliases.some((alias) => headerSet.has(alias)) ? 'valid' : 'invalid';
    return acc;
  }, {});
};

const resolveStatusValidation = (
  headers: string[],
  rows: string[][],
  draftMapping: Record<string, string>,
  draftStatusAliases: Record<string, string>,
): Record<string, ValidationStatus> => {
  const statusHeaderAliases = parseAliasList(draftMapping.status).map(normalizeHeader);
  const statusHeaderIndex = headers.findIndex((header) => statusHeaderAliases.includes(header));

  return statusFieldDefinitions.reduce<Record<string, ValidationStatus>>((acc, field) => {
    const aliases = parseAliasList(draftStatusAliases[field.key]).map(normalizeStatus);
    if (aliases.length === 0) {
      acc[field.key] = 'unknown';
      return acc;
    }
    if (statusHeaderIndex === -1) {
      acc[field.key] = 'invalid';
      return acc;
    }
    const matches = rows.slice(1, 201).some((row) => {
      if (statusHeaderIndex >= row.length) return false;
      const rawValue = row[statusHeaderIndex] ?? '';
      return aliases.includes(normalizeStatus(rawValue));
    });
    acc[field.key] = matches ? 'valid' : 'invalid';
    return acc;
  }, {});
};

const formatIssueList = (label: string, values: string[]): string | null => {
  if (values.length === 0) return null;
  const preview = values.slice(0, 3).join(', ');
  const suffix = values.length > 3 ? `, +${values.length - 3} more` : '';
  return `${label}: ${preview}${suffix}.`;
};

const resolveInvalidReason = (
  previewData: ReturnType<typeof buildValidationPreviewData>,
): string | null => {
  const missingRequiredHeaders = previewData.missingHeaderLabels.filter((label) =>
    requiredHeaderLabels.has(label),
  );
  if (missingRequiredHeaders.length > 0) {
    return getErrorCopy('upload.missingRequiredHeaders', {
      headers: missingRequiredHeaders.join(', '),
    });
  }
  return (
    formatIssueList('Missing test values', previewData.missingTestValues) ??
    formatIssueList('Missing suite values', previewData.missingSuiteValues) ??
    formatIssueList('Invalid statuses', previewData.invalidStatusValues) ??
    formatIssueList('Invalid durations', previewData.invalidDurationValues) ??
    formatIssueList('Invalid timestamps', previewData.invalidTimestampValues)
  );
};

export const validateResultsSpreadsheetFile = async (
  file: File,
  options?: ValidateResultsSpreadsheetFileOptions,
): Promise<ResultsSpreadsheetValidationResult> => {
  try {
    const rows = await parseValidationRows(file);
    if (rows.length === 0) {
      return { status: 'invalid', reason: 'Missing header row.' };
    }

    const resolvedHeaderAliases = options?.headerAliases ?? defaultCsvHeaderAliases;
    const resolvedStatusAliases = options?.statusAliases ?? defaultCsvStatusAliases;
    const draftMapping = buildHeaderMapping(resolvedHeaderAliases);
    const draftStatusAliases = buildStatusMapping(resolvedStatusAliases);
    const headers = rows[0] ?? [];
    const headerValidation = resolveHeaderValidation(headers, draftMapping);
    const statusValidation = resolveStatusValidation(
      headers.map(normalizeHeader),
      rows,
      draftMapping,
      draftStatusAliases,
    );
    const previewData = buildValidationPreviewData({
      headers,
      rows: rows.slice(1),
      headerDefinitions: fieldOptions,
      statusDefinitions: statusFieldDefinitions,
      headerValidation,
      statusValidation,
      draftMapping,
      draftStatusAliases,
      normalizeHeader,
      normalizeStatus,
      parseAliasList,
    });
    const invalidReason = resolveInvalidReason(previewData);

    return invalidReason
      ? { status: 'invalid', reason: invalidReason }
      : { status: 'valid', reason: null };
  } catch {
    return { status: 'invalid', reason: getErrorCopy('upload.readSpreadsheetFailed') };
  }
};

export const estimateResultsSpreadsheetFileRuns = async (
  file: File,
  options?: ValidateResultsSpreadsheetFileOptions,
): Promise<ResultsSpreadsheetRunEstimate | null> => {
  try {
    const rows = await parseValidationRows(file);
    if (rows.length === 0) return null;

    const resolvedHeaderAliases = options?.headerAliases ?? defaultCsvHeaderAliases;
    const resolvedStatusAliases = options?.statusAliases ?? defaultCsvStatusAliases;
    const draftMapping = buildHeaderMapping(resolvedHeaderAliases);
    const draftStatusAliases = buildStatusMapping(resolvedStatusAliases);
    const headers = rows[0] ?? [];
    const dataRows = rows.slice(1);
    if (dataRows.length === 0) {
      return { suiteRuns: 0, testRuns: 0 };
    }
    const headerValidation = resolveHeaderValidation(headers, draftMapping);
    const statusValidation = resolveStatusValidation(
      headers.map(normalizeHeader),
      rows,
      draftMapping,
      draftStatusAliases,
    );
    const previewData = buildValidationPreviewData({
      headers,
      rows: dataRows,
      headerDefinitions: fieldOptions,
      statusDefinitions: statusFieldDefinitions,
      headerValidation,
      statusValidation,
      draftMapping,
      draftStatusAliases,
      normalizeHeader,
      normalizeStatus,
      parseAliasList,
    });

    return {
      suiteRuns: previewData.totalSuites > 0 ? previewData.totalSuites : 1,
      testRuns: previewData.totalTests,
    };
  } catch {
    return null;
  }
};
